import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase/client/supabaseClient';
import { FiMapPin, FiUser, FiArrowRight, FiArrowLeft, FiCheck, FiLock } from 'react-icons/fi';
import IconWrapper from '../components/print-steps/IconWrapper';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { registerShop, ShopRegistrationData } from '../lib/supabase/api/register-shop';
import Canonical from '../components/Canonical';
import useKYCStore from '../store/kycStore';

interface LocationState {
  shopId?: string;
}

const ShopRegistrationPage: React.FC = () => {
  const { setShopAccountId } = useKYCStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [shopId, setShopId] = useState<string>('');
  const [shopName, setShopName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [coordinates, setCoordinates] = useState({ lat: '', lng: '' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  // Get shopId from URL param if available
  useEffect(() => {
    const state = location.state as LocationState;
    if (state && state.shopId) {
      setShopId(state.shopId);
    }
  }, [location.state]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const shopData: ShopRegistrationData = {
        shopId: shopId || null,
        name: shopName,
        ownerName: contactName,
        contactEmail: contactEmail,
        contactPhone: contactPhone,
        address: address,
        city: city,
        state: state || 'Not Specified',
        postalCode: postalCode,
        lat: coordinates.lat ? parseFloat(coordinates.lat) : null,
        lon: coordinates.lng ? parseFloat(coordinates.lng) : null,
        secret: password,
      };

      const response = await registerShop(shopData);

      if (response.success) {
        // Send registration confirmation email
        setShopAccountId(response.shop_id); // Set shop account ID in store
        try {
          const mailResponse = await fetch('https://ctrlp-mail-server.vercel.app/api/mail/shop-registration-success', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: contactEmail,
              name: contactName,
              shopName: shopName,
              shopId: shopId || 'Pending Approval'
            }),
          });
          
          if (!mailResponse.ok) {
            console.error('Failed to send registration confirmation email');
          }
        } catch (emailError) {
          console.error('Failed to send email request:', emailError);
          // Continue with navigation even if email request fails
        }
        
        // Clear the shop account ID from KYC store to allow new registrations
        setShopAccountId('');
        
        // Redirect to success page with the shop ID
        navigate('/shop-registration-success', {
          state: {
            shopId: shopId || null,
            shopAccountId: response.shop_id, // Pass shop_account_id for KYC
          }
        });
      }
    } catch (error: any) {
      console.error('Error submitting registration:', error);
      setErrorMessage(error.message || 'An error occurred during registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get current location
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          lat: position.coords.latitude.toString(),
          lng: position.coords.longitude.toString()
        });
        setLocationLoading(false);
      },
      (error) => {
        setErrorMessage(`Error getting location: ${error.message}`);
        setLocationLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  // Go to next step (with validation)
  const handleNextStep = () => {
    if (currentStep === 0) {
      // Validate first step
      if (!shopName || !contactName || !contactEmail || !contactPhone) {
        setErrorMessage('Please fill in all required fields');
        return;
      }
      // Email validation
      if (!/\S+@\S+\.\S+/.test(contactEmail)) {
        setErrorMessage('Please enter a valid email address');
        return;
      }
    } else if (currentStep === 1) {
      // Validate second step
      if (!address || !city || !postalCode) {
        setErrorMessage('Please fill in all address fields');
        return;
      }
    } else if (currentStep === 2) {
      // Validate password step
      if (!password || !confirmPassword) {
        setErrorMessage('Please enter password in both fields');
        return;
      }
      if (password.length < 8) {
        setErrorMessage('Password must be at least 8 characters long');
        return;
      }
      if (!/(?=.*[0-9])(?=.*[a-zA-Z])/.test(password)) {
        setErrorMessage('Password must contain at least one letter and one number');
        return;
      }
      if (password !== confirmPassword) {
        setErrorMessage('Passwords do not match');
        return;
      }
    }

    setErrorMessage('');
    setCurrentStep(currentStep + 1);
  };

  // Go to previous step
  const handlePrevStep = () => {
    setErrorMessage('');
    setCurrentStep(currentStep - 1);
  };

  // Render step indicators
  const renderStepIndicators = () => {
    return (
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          {['Basic Info', 'Shop Address', 'Security'].map((step, index) => (
            <React.Fragment key={index}>
              <div 
                className={`rounded-full h-10 w-10 flex items-center justify-center ${
                  index === currentStep 
                    ? 'bg-blue-600 text-white' 
                    : index < currentStep 
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                }`}
              >
                {index < currentStep ? (
                  <IconWrapper icon={FiCheck} className="h-5 w-5" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              {index < 2 && (
                <div className={`w-10 h-1 ${
                  index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <IconWrapper icon={FiUser} className="text-blue-600 mr-2" />
              <h3 className="text-xl font-semibold">Basic Information</h3>
            </div>

            {shopId ? (
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">QR Code Identifier</label>
              <input
                type="text"
                value={shopId}
                onChange={(e) => setShopId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                readOnly
              />
              </div>
            ) : (
                <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
                <p className="text-sm text-yellow-700">
                  The team will verify your request once the procedure is completed.
                </p>
                </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
              <input
                type="text"
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Enter the name of your shop as it should appear to customers</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <IconWrapper icon={FiMapPin} className="text-blue-600 mr-2" />
              <h3 className="text-xl font-semibold">Shop Location</h3>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <div className="flex items-center text-sm text-blue-700">
                <span className="font-medium mr-1">{shopName}</span>
                <span className="text-blue-400 mx-1">•</span>
                <span className="text-blue-600 text-xs">{contactEmail}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Maharashtra"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">GPS Coordinates (Optional)</label>
                <button
                  type="button"
                  onClick={handleGetLocation}
                  disabled={locationLoading}
                  className={`text-xs flex items-center px-3 py-1.5 rounded ${
                    locationLoading 
                      ? 'bg-gray-300 text-gray-600' 
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {locationLoading ? (
                    <>
                      <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-600 border-t-transparent animate-spin mr-2"></span>
                      Getting location...
                    </>
                  ) : (
                    <>
                      <IconWrapper icon={FiMapPin} className="mr-1 h-3 w-3" />
                      Use Current Location
                    </>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder="Latitude"
                    value={coordinates.lat}
                    onChange={(e) => setCoordinates({...coordinates, lat: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Longitude"
                    value={coordinates.lng}
                    onChange={(e) => setCoordinates({...coordinates, lng: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {coordinates.lat && coordinates.lng && (
                <p className="text-xs text-green-600 mt-1">
                  Location successfully captured!
                </p>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <IconWrapper icon={FiLock} className="text-blue-600 mr-2" />
              <h3 className="text-xl font-semibold">Security Setup</h3>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <div className="flex items-center text-sm text-blue-700">
                <span className="font-medium mr-1">{shopName}</span>
                <span className="text-blue-400 mx-1">•</span>
                <span className="text-blue-600 text-xs">{contactEmail}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters with numbers and letters</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-md">
              <h4 className="text-sm font-medium text-blue-700 mb-2">Registration Summary</h4>
              <ul className="text-xs text-blue-600 space-y-1">
                <li><span className="font-medium">Shop:</span> {shopName}</li>
                <li><span className="font-medium">Contact:</span> {contactName}</li>
                <li><span className="font-medium">Location:</span> {address}, {city}</li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderButtons = () => {
    return (
      <div className="flex justify-between mt-6">
        {currentStep > 0 ? (
          <button
            type="button"
            onClick={handlePrevStep}
            className="flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
          >
            <IconWrapper icon={FiArrowLeft} className="mr-2" /> Back
          </button>
        ) : (
          <div></div>  
        )}
        
        {currentStep < 2 ? (
          <button
            type="button"
            onClick={handleNextStep}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Next <IconWrapper icon={FiArrowRight} className="ml-2" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={isLoading}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isLoading ? (
              <>
                <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
                Registering...
              </>
            ) : (
              <>
                <IconWrapper icon={FiCheck} className="mr-2" /> Complete Registration
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Canonical url="https://ctrlp.co.in/shop-registration" />
      <Header />
      
      <main className="flex-grow flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-lg">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-gray-800">Shop Registration</h1>
            <p className="text-gray-600 mt-1">Join our network of print shops</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
            {(errorMessage || successMessage) && (
              <div 
                className={`p-4 ${
                  errorMessage 
                    ? 'bg-red-50 border-l-4 border-red-400' 
                    : 'bg-green-50 border-l-4 border-green-400'
                }`}
              >
                <div className="flex">
                  <div className="ml-3">
                    <p className={`text-sm ${errorMessage ? 'text-red-700' : 'text-green-700'}`}>
                      {errorMessage || successMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6">
              {renderStepIndicators()}
              
              <form onSubmit={handleSubmit}>
                {renderStepContent()}
                {renderButtons()}
              </form>
            </div>
          </div>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Already have a shop account? <button onClick={() => navigate('/kyc-form')} className="text-blue-600 hover:text-blue-800 hover:underline">Sign in here</button></p>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShopRegistrationPage;
